import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Building2, Upload, Save, Loader2, Bot, Sparkles, Clock, Plus, Trash2, Calendar, ToggleLeft, RotateCcw, Menu as MenuIcon, Layers, Box, Users, Shield, Mail, Bell } from "lucide-react";
import { toast } from "sonner";
import { useFeatures } from "../context/FeatureContext";
import { OpenClawSettings } from "../components/settings/OpenClawSettings";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_OF_WEEK = [
  { value: 0, label: "Monday" },
  { value: 1, label: "Tuesday" },
  { value: 2, label: "Wednesday" },
  { value: 3, label: "Thursday" },
  { value: 4, label: "Friday" },
  { value: 5, label: "Saturday" },
  { value: 6, label: "Sunday" },
];

// Helper: resolve background styling for permission toggle rows.
// Replaces nested ternaries with clear named states.
const getPermissionRowClass = (
  enabled,
  hasAccess,
  denyStyle = "bg-slate-50 border-slate-200",
  disabledStyle = "bg-slate-100 border-slate-200 opacity-50",
) => {
  if (!enabled) return disabledStyle;
  if (hasAccess) return "bg-green-50 border-green-200";
  return denyStyle;
};

const Settings = () => {
  const [settings, setSettings] = useState({
    company_name: "",
    logo_url: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "South Africa",
    phone: "",
    email: "",
    vat_number: "",
    registration_number: "",
    ai_enabled: false,
    // Email notification settings
    smtp_host: "",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: "",
    smtp_use_tls: true,
    notification_sender_email: "",
    notification_sender_name: "TaskForge Notifications",
    email_template_booking_confirmed: "Your booking has been confirmed for {service_name} on {booking_date}.",
    email_template_booking_rescheduled: "Your booking has been rescheduled to {booking_date}.",
    email_template_booking_cancelled: "Your booking for {service_name} has been cancelled.",
    email_template_rental_reserved: "Your rental for {item_name} has been reserved.",
    email_template_rental_delivered: "Your rental item {item_name} has been delivered.",
    email_template_return_request_approved: "Your return request for {item_name} has been approved. Collection scheduled for {collection_date}.",
    email_template_return_request_declined: "Your return request for {item_name} has been declined.",
  });
  const [timeSlots, setTimeSlots] = useState([]);
  const [workingDays, setWorkingDays] = useState([0, 1, 2, 3, 4]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSlots, setSavingSlots] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [togglingAI, setTogglingAI] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings`, { withCredentials: true });
      setSettings((prev) => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTimeSlots = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/time-slots`, { withCredentials: true });
      setTimeSlots(response.data.time_slots || []);
      setWorkingDays(response.data.working_days || [0, 1, 2, 3, 4]);
    } catch (error) {
      console.error("Error fetching time slots:", error);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchTimeSlots();
  }, [fetchSettings, fetchTimeSlots]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { logo_url, ...settingsToSave } = settings;
      await axios.put(`${API}/settings`, settingsToSave, { withCredentials: true });
      toast.success("Settings saved successfully");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTimeSlots = async () => {
    setSavingSlots(true);
    try {
      await axios.put(
        `${API}/settings/time-slots`,
        {
          time_slots: timeSlots,
          working_days: workingDays,
        },
        { withCredentials: true }
      );
      toast.success("Service schedule updated successfully");
    } catch (error) {
      console.error("Error saving time slots:", error);
      toast.error(error.response?.data?.detail || "Failed to save time slots");
    } finally {
      setSavingSlots(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("File size must be less than 2MB");
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(`${API}/settings/logo`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      setSettings((prev) => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success("Logo uploaded successfully");
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleAIToggle = async (enabled) => {
    setTogglingAI(true);
    try {
      await axios.put(`${API}/settings`, { ai_enabled: enabled }, { withCredentials: true });
      setSettings((prev) => ({ ...prev, ai_enabled: enabled }));
      toast.success(enabled ? "AI Assistant enabled" : "AI Assistant disabled");
    } catch (error) {
      console.error("Error toggling AI:", error);
      toast.error("Failed to update AI settings");
    } finally {
      setTogglingAI(false);
    }
  };

  const addTimeSlot = () => {
    setTimeSlots([...timeSlots, { start_time: "09:00", end_time: "11:00" }]);
  };

  const removeTimeSlot = (index) => {
    setTimeSlots(timeSlots.filter((_, i) => i !== index));
  };

  const updateTimeSlot = (index, field, value) => {
    const updated = [...timeSlots];
    updated[index] = { ...updated[index], [field]: value };
    setTimeSlots(updated);
  };

  const toggleWorkingDay = (day) => {
    if (workingDays.includes(day)) {
      setWorkingDays(workingDays.filter((d) => d !== day));
    } else {
      setWorkingDays([...workingDays, day].sort());
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-96 bg-slate-200 rounded-sm"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">
          Settings
        </h1>
        <p className="text-slate-500 mt-1">
          Configure your company information, service schedule, and features
        </p>
      </div>

      <Tabs defaultValue="general" className="max-w-4xl">
        <TabsList className="mb-6">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="schedule">Service Schedule</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="notifications-tab">Email Notifications</TabsTrigger>
          <TabsTrigger value="features" data-testid="features-tab">Features</TabsTrigger>
          <TabsTrigger value="permissions" data-testid="permissions-tab">Role Permissions</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="integrations-tab">Integrations</TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general">
          <div className="grid gap-6">
            {/* AI Assistant Settings */}
            <Card className="rounded-sm border-slate-200 bg-gradient-to-r from-slate-50 to-indigo-50">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Bot className="h-5 w-5 text-indigo-600" />
                  <CardTitle className="text-lg">AI Stock Controller</CardTitle>
                </div>
                <CardDescription>
                  Enable AI-powered inventory analysis and smart reorder suggestions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="font-medium text-slate-900 flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-indigo-500" />
                      Enable AI Assistant
                    </p>
                    <p className="text-sm text-slate-500">
                      When enabled, AI will analyze stock levels, suggest reorders, and provide a chat interface
                    </p>
                  </div>
                  <Switch
                    checked={settings.ai_enabled}
                    onCheckedChange={handleAIToggle}
                    disabled={togglingAI}
                    data-testid="ai-toggle"
                  />
                </div>
                {settings.ai_enabled && (
                  <div className="mt-4 p-3 bg-indigo-100 rounded-sm">
                    <p className="text-sm text-indigo-800">
                      <strong>AI is active!</strong> Visit the Dashboard to use the AI chat and view suggestions.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Company Logo */}
            <Card className="rounded-sm border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Company Logo</CardTitle>
                <CardDescription>
                  This logo will appear on your purchase orders
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-6">
                  <div className="w-32 h-32 bg-slate-100 rounded-sm flex items-center justify-center overflow-hidden border border-slate-200">
                    {settings.logo_url ? (
                      <img
                        src={settings.logo_url}
                        alt="Company Logo"
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Building2 className="h-12 w-12 text-slate-400" />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="logo-upload"
                      className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-sm hover:bg-slate-800 transition-colors"
                    >
                      {uploadingLogo ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4" />
                      )}
                      {uploadingLogo ? "Uploading..." : "Upload Logo"}
                    </Label>
                    <Input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoUpload}
                      disabled={uploadingLogo}
                      data-testid="logo-upload-input"
                    />
                    <p className="text-sm text-slate-500">
                      PNG, JPG or SVG. Max 2MB.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Company Information Form */}
            <form onSubmit={handleSave}>
              <Card className="rounded-sm border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg">Company Information</CardTitle>
                  <CardDescription>
                    Your company details for official documents
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company_name">Company Name *</Label>
                      <Input
                        id="company_name"
                        value={settings.company_name}
                        onChange={(e) =>
                          setSettings({ ...settings, company_name: e.target.value })
                        }
                        placeholder="Your Company Name"
                        className="rounded-sm"
                        data-testid="company-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={settings.email || ""}
                        onChange={(e) =>
                          setSettings({ ...settings, email: e.target.value })
                        }
                        placeholder="company@example.com"
                        className="rounded-sm"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        value={settings.phone || ""}
                        onChange={(e) =>
                          setSettings({ ...settings, phone: e.target.value })
                        }
                        placeholder="+27 XX XXX XXXX"
                        className="rounded-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="vat_number">VAT Number</Label>
                      <Input
                        id="vat_number"
                        value={settings.vat_number || ""}
                        onChange={(e) =>
                          setSettings({ ...settings, vat_number: e.target.value })
                        }
                        placeholder="VAT registration number"
                        className="rounded-sm"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="registration_number">Company Registration Number</Label>
                    <Input
                      id="registration_number"
                      value={settings.registration_number || ""}
                      onChange={(e) =>
                        setSettings({ ...settings, registration_number: e.target.value })
                      }
                      placeholder="e.g., 2024/123456/07"
                      className="rounded-sm"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Address */}
              <Card className="rounded-sm border-slate-200 mt-6">
                <CardHeader>
                  <CardTitle className="text-lg">Business Address</CardTitle>
                  <CardDescription>
                    This address will appear on purchase orders
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="address_line1">Address Line 1</Label>
                    <Input
                      id="address_line1"
                      value={settings.address_line1 || ""}
                      onChange={(e) =>
                        setSettings({ ...settings, address_line1: e.target.value })
                      }
                      placeholder="Street address"
                      className="rounded-sm"
                      data-testid="address-line1-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address_line2">Address Line 2</Label>
                    <Input
                      id="address_line2"
                      value={settings.address_line2 || ""}
                      onChange={(e) =>
                        setSettings({ ...settings, address_line2: e.target.value })
                      }
                      placeholder="Suite, floor, etc."
                      className="rounded-sm"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={settings.city || ""}
                        onChange={(e) =>
                          setSettings({ ...settings, city: e.target.value })
                        }
                        placeholder="City"
                        className="rounded-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State/Province</Label>
                      <Input
                        id="state"
                        value={settings.state || ""}
                        onChange={(e) =>
                          setSettings({ ...settings, state: e.target.value })
                        }
                        placeholder="Province"
                        className="rounded-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="postal_code">Postal Code</Label>
                      <Input
                        id="postal_code"
                        value={settings.postal_code || ""}
                        onChange={(e) =>
                          setSettings({ ...settings, postal_code: e.target.value })
                        }
                        placeholder="Postal code"
                        className="rounded-sm"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">Country</Label>
                    <Input
                      id="country"
                      value={settings.country || ""}
                      onChange={(e) =>
                        setSettings({ ...settings, country: e.target.value })
                      }
                      placeholder="South Africa"
                      className="rounded-sm"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Save Button */}
              <div className="flex justify-end mt-6">
                <Button
                  type="submit"
                  className="rounded-sm bg-slate-900 hover:bg-slate-800"
                  disabled={saving}
                  data-testid="save-settings-btn"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  {saving ? "Saving..." : "Save Company Info"}
                </Button>
              </div>
            </form>
          </div>
        </TabsContent>

        {/* Schedule Tab */}
        <TabsContent value="schedule">
          <Card className="rounded-sm border-slate-200">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-emerald-600" />
                <CardTitle className="text-lg">Service Schedule</CardTitle>
              </div>
              <CardDescription>
                Configure available time slots and working days for service bookings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Working Days */}
              <div className="space-y-3">
                <Label className="text-base font-medium">Working Days</Label>
                <div className="flex flex-wrap gap-3">
                  {DAYS_OF_WEEK.map((day) => (
                    <div key={day.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`day-${day.value}`}
                        checked={workingDays.includes(day.value)}
                        onCheckedChange={() => toggleWorkingDay(day.value)}
                        data-testid={`day-${day.value}-checkbox`}
                      />
                      <Label
                        htmlFor={`day-${day.value}`}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {day.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Time Slots */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">Available Time Slots</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addTimeSlot}
                    className="rounded-sm"
                    data-testid="add-slot-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Slot
                  </Button>
                </div>
                
                {timeSlots.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center border border-dashed border-slate-300 rounded-sm">
                    No time slots configured. Add a slot to enable service bookings.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {timeSlots.map((slot, index) => (
                      <div
                        key={`slot-${slot.start_time}-${index}`}
                        className="flex items-center gap-3 p-3 bg-slate-50 rounded-sm"
                      >
                        <Clock className="h-4 w-4 text-slate-400" />
                        <div className="flex items-center gap-2 flex-1">
                          <Input
                            type="time"
                            value={slot.start_time}
                            onChange={(e) => updateTimeSlot(index, "start_time", e.target.value)}
                            className="rounded-sm w-32"
                            data-testid={`slot-${index}-start`}
                          />
                          <span className="text-slate-500">to</span>
                          <Input
                            type="time"
                            value={slot.end_time}
                            onChange={(e) => updateTimeSlot(index, "end_time", e.target.value)}
                            className="rounded-sm w-32"
                            data-testid={`slot-${index}-end`}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeTimeSlot(index)}
                          className="h-8 w-8 text-slate-400 hover:text-red-600"
                          data-testid={`remove-slot-${index}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button
                type="button"
                onClick={handleSaveTimeSlots}
                disabled={savingSlots}
                className="rounded-sm bg-emerald-600 hover:bg-emerald-700"
                data-testid="save-schedule-btn"
              >
                {savingSlots ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {savingSlots ? "Saving..." : "Save Schedule"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Notifications Tab */}
        <TabsContent value="notifications">
          <div className="space-y-6">
            {/* SMTP Configuration */}
            <Card className="rounded-sm border-slate-200">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-slate-500" />
                  <CardTitle className="text-lg">Email Configuration</CardTitle>
                </div>
                <CardDescription>Configure SMTP settings for sending customer notifications. Leave blank to use Resend API key from environment.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="notification_sender_email">Sender Email</Label>
                    <Input
                      id="notification_sender_email"
                      placeholder="notifications@yourcompany.com"
                      value={settings.notification_sender_email || ""}
                      onChange={(e) => setSettings({ ...settings, notification_sender_email: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notification_sender_name">Sender Name</Label>
                    <Input
                      id="notification_sender_name"
                      placeholder="TaskForge Notifications"
                      value={settings.notification_sender_name || ""}
                      onChange={(e) => setSettings({ ...settings, notification_sender_name: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <p className="text-sm font-medium mb-4">SMTP Settings (Optional)</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="smtp_host">SMTP Host</Label>
                      <Input
                        id="smtp_host"
                        placeholder="smtp.gmail.com"
                        value={settings.smtp_host || ""}
                        onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                        className="rounded-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="smtp_port">SMTP Port</Label>
                      <Input
                        id="smtp_port"
                        type="number"
                        placeholder="587"
                        value={settings.smtp_port || 587}
                        onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) || 587 })}
                        className="rounded-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="smtp_username">SMTP Username</Label>
                      <Input
                        id="smtp_username"
                        placeholder="your@email.com"
                        value={settings.smtp_username || ""}
                        onChange={(e) => setSettings({ ...settings, smtp_username: e.target.value })}
                        className="rounded-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="smtp_password">SMTP Password</Label>
                      <Input
                        id="smtp_password"
                        type="password"
                        placeholder="••••••••"
                        value={settings.smtp_password || ""}
                        onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                        className="rounded-sm"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-4">
                    <Switch
                      id="smtp_use_tls"
                      checked={settings.smtp_use_tls !== false}
                      onCheckedChange={(checked) => setSettings({ ...settings, smtp_use_tls: checked })}
                    />
                    <Label htmlFor="smtp_use_tls">Use TLS</Label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Email Templates */}
            <Card className="rounded-sm border-slate-200">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-slate-500" />
                  <CardTitle className="text-lg">Email Templates</CardTitle>
                </div>
                <CardDescription>Customize the notification messages sent to customers. Use placeholders like {"{service_name}"}, {"{booking_date}"}, {"{item_name}"}.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="tpl_booking_confirmed">Booking Confirmed</Label>
                  <Input
                    id="tpl_booking_confirmed"
                    value={settings.email_template_booking_confirmed || ""}
                    onChange={(e) => setSettings({ ...settings, email_template_booking_confirmed: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tpl_booking_rescheduled">Booking Rescheduled</Label>
                  <Input
                    id="tpl_booking_rescheduled"
                    value={settings.email_template_booking_rescheduled || ""}
                    onChange={(e) => setSettings({ ...settings, email_template_booking_rescheduled: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tpl_booking_cancelled">Booking Cancelled</Label>
                  <Input
                    id="tpl_booking_cancelled"
                    value={settings.email_template_booking_cancelled || ""}
                    onChange={(e) => setSettings({ ...settings, email_template_booking_cancelled: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tpl_rental_reserved">Rental Reserved</Label>
                  <Input
                    id="tpl_rental_reserved"
                    value={settings.email_template_rental_reserved || ""}
                    onChange={(e) => setSettings({ ...settings, email_template_rental_reserved: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tpl_rental_delivered">Rental Delivered</Label>
                  <Input
                    id="tpl_rental_delivered"
                    value={settings.email_template_rental_delivered || ""}
                    onChange={(e) => setSettings({ ...settings, email_template_rental_delivered: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tpl_return_approved">Return Request Approved</Label>
                  <Input
                    id="tpl_return_approved"
                    value={settings.email_template_return_request_approved || ""}
                    onChange={(e) => setSettings({ ...settings, email_template_return_request_approved: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tpl_return_declined">Return Request Declined</Label>
                  <Input
                    id="tpl_return_declined"
                    value={settings.email_template_return_request_declined || ""}
                    onChange={(e) => setSettings({ ...settings, email_template_return_request_declined: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="rounded-sm"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Email Settings
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Features Tab */}
        <TabsContent value="features">
          <FeatureSettings />
        </TabsContent>

        {/* Role Permissions Tab */}
        <TabsContent value="permissions">
          <RolePermissionsSettings />
        </TabsContent>

        <TabsContent value="integrations">
          <OpenClawSettings />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Feature Settings Component
const FeatureSettings = () => {
  const { features, updateFeatures, resetFeatures, refreshFeatures } = useFeatures();
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [localFeatures, setLocalFeatures] = useState(features);

  useEffect(() => {
    setLocalFeatures(features);
  }, [features, setLocalFeatures]);

  const handleSectionToggle = (sectionKey, enabled) => {
    setLocalFeatures(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionKey]: { ...prev.sections[sectionKey], enabled }
      }
    }));
  };

  const handleMenuItemToggle = (itemKey, enabled) => {
    setLocalFeatures(prev => ({
      ...prev,
      menu_items: {
        ...prev.menu_items,
        [itemKey]: { ...prev.menu_items[itemKey], enabled }
      }
    }));
  };

  const handleModuleToggle = (moduleKey, enabled) => {
    setLocalFeatures(prev => ({
      ...prev,
      modules: {
        ...prev.modules,
        [moduleKey]: { ...prev.modules[moduleKey], enabled }
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateFeatures(localFeatures);
      toast.success("Feature settings saved successfully");
    } catch (error) {
      toast.error("Failed to save feature settings");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Are you sure you want to reset all features to default? This will enable all features.")) {
      return;
    }
    setResetting(true);
    try {
      await resetFeatures();
      toast.success("Features reset to defaults");
    } catch (error) {
      toast.error("Failed to reset features");
    } finally {
      setResetting(false);
    }
  };

  // Group menu items by section
  const groupedMenuItems = {};
  Object.entries(localFeatures.menu_items || {}).forEach(([key, item]) => {
    const section = item.section || "other";
    if (!groupedMenuItems[section]) {
      groupedMenuItems[section] = [];
    }
    groupedMenuItems[section].push({ key, ...item });
  });

  return (
    <div className="space-y-6">
      {/* Modules */}
      <Card className="rounded-sm border-slate-200">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Box className="h-5 w-5 text-purple-600" />
            <CardTitle className="text-lg">Modules</CardTitle>
          </div>
          <CardDescription>
            Enable or disable entire feature modules
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(localFeatures.modules || {}).map(([key, module]) => (
              <div
                key={key}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-sm"
              >
                <div>
                  <p className="font-medium text-sm">{module.label}</p>
                </div>
                <Switch
                  checked={module.enabled}
                  onCheckedChange={(enabled) => handleModuleToggle(key, enabled)}
                  data-testid={`module-${key}-toggle`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Sections */}
      <Card className="rounded-sm border-slate-200">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-lg">Navigation Sections</CardTitle>
          </div>
          <CardDescription>
            Show or hide entire navigation sections
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {Object.entries(localFeatures.sections || {}).map(([key, section]) => (
              <div
                key={key}
                className={`flex items-center justify-between p-3 rounded-sm border ${
                  section.enabled ? "bg-white border-slate-200" : "bg-slate-100 border-slate-200"
                }`}
              >
                <span className="font-medium text-sm">{section.label}</span>
                <Switch
                  checked={section.enabled}
                  onCheckedChange={(enabled) => handleSectionToggle(key, enabled)}
                  data-testid={`section-${key}-toggle`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Menu Items */}
      <Card className="rounded-sm border-slate-200">
        <CardHeader>
          <div className="flex items-center gap-2">
            <MenuIcon className="h-5 w-5 text-emerald-600" />
            <CardTitle className="text-lg">Menu Items</CardTitle>
          </div>
          <CardDescription>
            Toggle individual menu items within each section
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {Object.entries(groupedMenuItems).map(([section, items]) => {
              const sectionInfo = localFeatures.sections?.[section];
              const sectionEnabled = sectionInfo?.enabled ?? true;
              
              return (
                <div key={section} className={!sectionEnabled ? "opacity-50" : ""}>
                  <h4 className="font-medium text-sm text-slate-700 mb-2 flex items-center gap-2">
                    {sectionInfo?.label || section}
                    {!sectionEnabled && (
                      <span className="text-xs text-slate-400">(Section disabled)</span>
                    )}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {items.map((item) => (
                      <div
                        key={item.key}
                        className={`flex items-center justify-between p-2 rounded-sm border ${
                          item.enabled && sectionEnabled 
                            ? "bg-white border-slate-200" 
                            : "bg-slate-50 border-slate-100"
                        }`}
                      >
                        <span className="text-sm">{item.label}</span>
                        <Switch
                          checked={item.enabled}
                          onCheckedChange={(enabled) => handleMenuItemToggle(item.key, enabled)}
                          disabled={!sectionEnabled}
                          data-testid={`menu-${item.key}-toggle`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={handleReset}
          disabled={resetting}
          className="rounded-sm"
          data-testid="reset-features-btn"
        >
          {resetting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RotateCcw className="h-4 w-4 mr-2" />
          )}
          Reset to Defaults
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="rounded-sm bg-slate-900 hover:bg-slate-800"
          data-testid="save-features-btn"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {saving ? "Saving..." : "Save Feature Settings"}
        </Button>
      </div>
    </div>
  );
};

// Role Permissions Settings Component
const RolePermissionsSettings = () => {
  const { features, rolePermissions, updateRolePermissions, resetRolePermissions, refreshFeatures } = useFeatures();
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [localPermissions, setLocalPermissions] = useState({});
  const [activeRole, setActiveRole] = useState("worker");

  useEffect(() => {
    setLocalPermissions(rolePermissions);
  }, [rolePermissions, setLocalPermissions]);

  const handleSectionToggle = (role, sectionKey, enabled) => {
    setLocalPermissions(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        sections: {
          ...prev[role]?.sections,
          [sectionKey]: enabled
        }
      }
    }));
  };

  const handleMenuItemToggle = (role, itemKey, enabled) => {
    setLocalPermissions(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        menu_items: {
          ...prev[role]?.menu_items,
          [itemKey]: enabled
        }
      }
    }));
  };

  const handleModuleToggle = (role, moduleKey, enabled) => {
    setLocalPermissions(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        modules: {
          ...prev[role]?.modules,
          [moduleKey]: enabled
        }
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save permissions for both roles
      for (const role of ["worker", "manager"]) {
        if (localPermissions[role]) {
          await updateRolePermissions(role, localPermissions[role]);
        }
      }
      toast.success("Role permissions saved successfully");
      refreshFeatures();
    } catch (error) {
      toast.error("Failed to save role permissions");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Are you sure you want to reset all role permissions to default? This will remove all access for technicians and managers.")) {
      return;
    }
    setResetting(true);
    try {
      await resetRolePermissions();
      toast.success("Role permissions reset to defaults");
      refreshFeatures();
    } catch (error) {
      toast.error("Failed to reset role permissions");
    } finally {
      setResetting(false);
    }
  };

  // Group menu items by section for display
  const groupedMenuItems = {};
  Object.entries(features.menu_items || {}).forEach(([key, item]) => {
    const section = item.section || "other";
    if (!groupedMenuItems[section]) {
      groupedMenuItems[section] = [];
    }
    groupedMenuItems[section].push({ key, ...item });
  });

  const rolePerms = localPermissions[activeRole] || { sections: {}, menu_items: {}, modules: {} };

  return (
    <div className="space-y-6">
      {/* Info Card */}
      <Card className="rounded-sm border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Role-Based Access Control</p>
              <p className="text-sm text-blue-700 mt-1">
                Configure which features each role can access. <strong>Admins always have full access.</strong> Global feature toggles take precedence - if a feature is globally disabled, it won't be available to any role.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Role Selector */}
      <div className="flex gap-2">
        <Button
          variant={activeRole === "worker" ? "default" : "outline"}
          onClick={() => setActiveRole("worker")}
          className="rounded-sm"
        >
          <Users className="h-4 w-4 mr-2" />
          Technician
        </Button>
        <Button
          variant={activeRole === "manager" ? "default" : "outline"}
          onClick={() => setActiveRole("manager")}
          className="rounded-sm"
        >
          <Users className="h-4 w-4 mr-2" />
          Manager
        </Button>
      </div>

      {/* Modules */}
      <Card className="rounded-sm border-slate-200">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Box className="h-5 w-5 text-purple-600" />
            <CardTitle className="text-lg">Module Access for {activeRole === "worker" ? "Technicians" : "Managers"}</CardTitle>
          </div>
          <CardDescription>
            Allow or deny access to feature modules
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(features.modules || {}).map(([key, module]) => {
              const globalEnabled = module.enabled;
              const roleHasAccess = rolePerms.modules?.[key] ?? false;
              
              return (
                <div
                  key={key}
                  className={`flex items-center justify-between p-3 rounded-sm border ${getPermissionRowClass(globalEnabled, roleHasAccess)}`}
                >
                  <div>
                    <p className="font-medium text-sm">{module.label}</p>
                    {!globalEnabled && (
                      <p className="text-xs text-slate-500">Globally disabled</p>
                    )}
                  </div>
                  <Switch
                    checked={roleHasAccess}
                    onCheckedChange={(enabled) => handleModuleToggle(activeRole, key, enabled)}
                    disabled={!globalEnabled}
                    data-testid={`role-module-${key}-toggle`}
                  />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Sections */}
      <Card className="rounded-sm border-slate-200">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-lg">Section Access for {activeRole === "worker" ? "Technicians" : "Managers"}</CardTitle>
          </div>
          <CardDescription>
            Allow or deny access to navigation sections
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {Object.entries(features.sections || {}).map(([key, section]) => {
              const globalEnabled = section.enabled;
              const roleHasAccess = rolePerms.sections?.[key] ?? false;
              
              return (
                <div
                  key={key}
                  className={`flex items-center justify-between p-3 rounded-sm border ${getPermissionRowClass(globalEnabled, roleHasAccess, "bg-white border-slate-200")}`}
                >
                  <span className="font-medium text-sm">{section.label}</span>
                  <Switch
                    checked={roleHasAccess}
                    onCheckedChange={(enabled) => handleSectionToggle(activeRole, key, enabled)}
                    disabled={!globalEnabled}
                    data-testid={`role-section-${key}-toggle`}
                  />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Menu Items */}
      <Card className="rounded-sm border-slate-200">
        <CardHeader>
          <div className="flex items-center gap-2">
            <MenuIcon className="h-5 w-5 text-emerald-600" />
            <CardTitle className="text-lg">Menu Item Access for {activeRole === "worker" ? "Technicians" : "Managers"}</CardTitle>
          </div>
          <CardDescription>
            Toggle individual menu items. Section access must be enabled first.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {Object.entries(groupedMenuItems).map(([section, items]) => {
              const sectionInfo = features.sections?.[section];
              const globalSectionEnabled = sectionInfo?.enabled ?? true;
              const roleSectionEnabled = rolePerms.sections?.[section] ?? false;
              const effectiveEnabled = globalSectionEnabled && roleSectionEnabled;
              
              return (
                <div key={section} className={!effectiveEnabled ? "opacity-50" : ""}>
                  <h4 className="font-medium text-sm text-slate-700 mb-2 flex items-center gap-2">
                    {sectionInfo?.label || section}
                    {!globalSectionEnabled && (
                      <span className="text-xs text-slate-400">(Globally disabled)</span>
                    )}
                    {globalSectionEnabled && !roleSectionEnabled && (
                      <span className="text-xs text-amber-600">(Section access denied)</span>
                    )}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {items.map((item) => {
                      const globalItemEnabled = item.enabled;
                      const roleHasAccess = rolePerms.menu_items?.[item.key] ?? false;
                      const canToggle = globalItemEnabled && effectiveEnabled;
                      
                      return (
                        <div
                          key={item.key}
                          className={`flex items-center justify-between p-2 rounded-sm border ${getPermissionRowClass(canToggle, roleHasAccess, "bg-white border-slate-200", "bg-slate-50 border-slate-100")}`}
                        >
                          <span className="text-sm">{item.label}</span>
                          <Switch
                            checked={roleHasAccess}
                            onCheckedChange={(enabled) => handleMenuItemToggle(activeRole, item.key, enabled)}
                            disabled={!canToggle}
                            data-testid={`role-menu-${item.key}-toggle`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={handleReset}
          disabled={resetting}
          className="rounded-sm"
          data-testid="reset-permissions-btn"
        >
          {resetting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RotateCcw className="h-4 w-4 mr-2" />
          )}
          Reset to Defaults
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="rounded-sm bg-slate-900 hover:bg-slate-800"
          data-testid="save-permissions-btn"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {saving ? "Saving..." : "Save Permissions"}
        </Button>
      </div>
    </div>
  );
};

export default Settings;
